import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function Projects() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [hoveredProject, setHoveredProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async (retries = 3, delay = 1000) => {
      try {
        const response = await axios.get(`${API_URL}/projects`);
        if (response.data && response.data.success) {
          setProjects(response.data.data || []);
        } else if (Array.isArray(response.data)) {
          setProjects(response.data);
        }
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 429 && retries > 0) {
          console.log(
            `Rate limited. Retrying in ${delay}ms... (${retries} attempts left)`
          );
          setTimeout(() => fetchProjects(retries - 1, delay * 2), delay);
          return;
        }
        console.error("Error fetching projects:", error);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Extract unique categories from projects
  const categories = ["All", ...new Set(projects.map((p) => p.category))];

  const filteredProjects =
    activeCategory === "All"
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center py-32 px-6">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          Loading...
        </div>
      </section>
    );
  }

  if (projects.length === 0) {
    return (
      <section className="min-h-screen flex items-center justify-center py-32 px-6">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          No projects found
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section indicator */}
        <div className="flex items-center gap-3 sm:gap-4 mb-10 sm:mb-14 md:mb-16">
          <div className="w-1 h-1 rounded-full bg-white/30"></div>
          <span className="text-white/30 text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light">
            Projects
          </span>
          <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-10 sm:mb-14 md:mb-16">
          {categories.map((cat, idx) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`relative px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-full transition-all duration-500 ${
                activeCategory === cat
                  ? "bg-white/[0.04] border border-white/[0.12]"
                  : "bg-white/[0.01] border border-white/[0.06] hover:bg-white/[0.02] hover:border-white/[0.08]"
              }`}
              style={{
                animation: `fadeInUp 0.6s ease-out ${idx * 0.05}s both`,
              }}
            >
              <span
                className={`text-xs sm:text-sm font-light tracking-wider uppercase transition-colors duration-500 ${
                  activeCategory === cat ? "text-white/70" : "text-white/30"
                }`}
              >
                {cat}
              </span>
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {filteredProjects.map((project, idx) => (
            <div
              key={project.title}
              onMouseEnter={() => setHoveredProject(project.title)}
              onMouseLeave={() => setHoveredProject(null)}
              className="group relative bg-white/[0.01] border border-white/[0.04] rounded-2xl p-4 sm:p-5 md:p-6 
                       hover:bg-white/[0.02] hover:border-white/[0.08] transition-all duration-500
                       flex flex-col"
              style={{
                animation: `fadeInUp 0.6s ease-out ${idx * 0.05}s both`,
              }}
            >
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-4">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full 
                              bg-white/[0.02] border border-white/[0.06]`}
                >
                  {/* Minimal Status Dot */}
                  <div className="relative">
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        project.status === "Completed"
                          ? "bg-green-400/60"
                          : "bg-red-400/60"
                      }`}
                      style={{
                        boxShadow:
                          project.status === "Completed"
                            ? "0 0 6px 1px rgba(74, 222, 128, 0.3), 0 0 10px 2px rgba(74, 222, 128, 0.15)"
                            : "0 0 6px 1px rgba(248, 113, 113, 0.3), 0 0 10px 2px rgba(248, 113, 113, 0.15)",
                      }}
                    ></div>
                    <div
                      className={`absolute inset-0 rounded-full animate-pulse ${
                        project.status === "Completed"
                          ? "bg-green-400/40"
                          : "bg-red-400/40"
                      }`}
                      style={{
                        boxShadow:
                          project.status === "Completed"
                            ? "0 0 8px 2px rgba(74, 222, 128, 0.2)"
                            : "0 0 8px 2px rgba(248, 113, 113, 0.2)",
                      }}
                    ></div>
                  </div>
                  <span className="text-white/30 text-[10px] uppercase tracking-wider font-light">
                    {project.status}
                  </span>
                </div>

                {/* Category Tag */}
                <span className="text-white/20 text-[10px] uppercase tracking-wider font-light">
                  {project.category}
                </span>
              </div>

              {/* Title */}
              <h3
                className={`text-lg font-light leading-tight mb-3 transition-colors duration-500 ${
                  hoveredProject === project.title
                    ? "text-white/70"
                    : "text-white/50"
                }`}
              >
                {project.title}
              </h3>

              {/* Description */}
              <p className="text-white/40 text-base sm:text-sm leading-relaxed font-light mb-6 flex-1">
                {project.description}
              </p>

              {/* Tech Stack */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(project.technologies || project.tech || []).map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-white/[0.02] border border-white/[0.04] rounded-full 
                             text-xs sm:text-[11px] text-white/40 font-light"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* GitHub Link */}
              {project.link || project.githubUrl ? (
                <a
                  href={project.link || project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link inline-flex items-center gap-2 text-white/40 
                           hover:text-white/70 transition-colors duration-500 text-sm font-light"
                >
                  <span>View on GitHub</span>
                  <svg
                    className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 text-white/20 text-sm font-light">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <span>Private Repository</span>
                </div>
              )}

              {/* Hover Gradient Effect */}
              <div
                className={`absolute inset-0 rounded-2xl bg-white/[0.01] opacity-0 
                            group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
              ></div>
            </div>
          ))}
        </div>

        {/* Project Count */}
        <div className="mt-16 text-center">
          <div
            className="inline-flex items-center gap-3 px-5 py-3 bg-white/[0.02] 
                        border border-white/[0.06] rounded-full"
          >
            <span className="text-white/30 text-xs tracking-wider uppercase font-light">
              Showing {filteredProjects.length}{" "}
              {activeCategory === "All"
                ? "Projects"
                : `${activeCategory} Projects`}
            </span>
          </div>
        </div>
      </div>

      {/* Floating accent */}
      <div
        className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-white/[0.008] 
                    blur-[150px] rounded-full"
      ></div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out both;
        }
      `}</style>
    </section>
  );
}

export default Projects;
